/**
 * Timeline view of the gated scoring pipeline.
 *
 * Stages render as vertical rows with total duration + cost per stage:
 *   - Summary (inferred — the summarizer runs inside the graph but
 *     doesn't write an AgentScore; we just note that it happened)
 *   - Triage — one row with tags/flags/verdict
 *   - Specialists — a collapsible batch; expanded shows per-agent rows
 *     with tokens + cost + model
 *   - Finalize — the meta-agent's synthesis
 *
 * The value here is *economics*: admins can see at a glance whether
 * short-circuits saved money, which specialists are expensive, and how
 * the triage + finalize calls compare to the specialist fan-out.
 */
import React, {useState} from 'react';
import {Box, Chip, Collapse, IconButton, Paper, Typography} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {AgentScore, EvalAgentEntry} from '../../../types/generated/graphql';
import {pcolors, cardSx, sectionLabelSx} from '../../../theme/planapusTheme';
import {humanizeSpecialization} from '../../Admin/agentLabels';
import {agentScoreTone, humanizeTag, humanizeFlag, verdictTone, useChainData} from './useChainData';

interface Props {
  scores: AgentScore[];
  agents: EvalAgentEntry[];
  childAge?: number;
}

// Use costEstimate.totalCost + tokenUsage.total when present; otherwise
// zero so the row still renders (no cached-hit oddities).
function costOf(s: AgentScore): number {
  return s.costEstimate?.totalCost ?? 0;
}
function tokensOf(s: AgentScore): number {
  return s.tokenUsage?.total ?? 0;
}

export function ScoringChainTimeline({scores, agents, childAge = 10}: Props) {
  const {
    triage,
    final,
    specialistScores: specialists,
    shortCircuited,
    didGate,
    channelGateScore,
  } = useChainData(scores, agents, childAge);

  if (channelGateScore) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          p: 2,
          bgcolor: pcolors.red[50],
          borderColor: pcolors.red[100],
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <BoltIcon sx={{fontSize: 20, color: pcolors.red[700]}} />
          <Typography sx={{fontSize: 14, fontWeight: 700, color: pcolors.red[700]}}>
            Channel gate — scoring skipped
          </Typography>
          <Box sx={{flex: 1}} />
          <Typography sx={{fontSize: 11, color: pcolors.green[700], fontWeight: 700}}>
            $0.0000
          </Typography>
        </Box>
        <Typography sx={{fontSize: 12, color: pcolors.textPrimary, lineHeight: 1.55}}>
          {channelGateScore.explanation}
        </Typography>
        <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 1}}>
          Every stage was bypassed — no triage, no specialists, no finalize —
          so the total LLM spend for this video was $0.
        </Typography>
      </Paper>
    );
  }

  const specialistCost = specialists.reduce((a, s) => a + costOf(s), 0);
  const specialistTokens = specialists.reduce((a, s) => a + tokensOf(s), 0);
  const triageCost = triage ? costOf(triage) : 0;
  const finalCost = final ? costOf(final) : 0;
  const totalCost = triageCost + specialistCost + finalCost;

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
      {/* Header — total cost + short-circuit banner. */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        <Typography sx={sectionLabelSx}>Execution timeline</Typography>
        <Box sx={{flex: 1}} />
        {totalCost > 0 && (
          <Chip
            size="small"
            label={`$${totalCost.toFixed(4)} total`}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              bgcolor: pcolors.green[50],
              color: pcolors.green[700],
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        )}
      </Box>

      {shortCircuited && (
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: 1.25,
            bgcolor: pcolors.amber[50],
            borderColor: pcolors.amber[100],
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <BoltIcon sx={{fontSize: 18, color: pcolors.amber[700]}} />
          <Typography sx={{fontSize: 12, fontWeight: 700, color: pcolors.amber[700]}}>
            Short-circuited after triage — specialists + finalize skipped.
          </Typography>
        </Paper>
      )}

      {/* Legacy fallback. */}
      {!didGate && (
        <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>
          Legacy score — no triage/finalize stages were recorded for this video.
        </Typography>
      )}

      {/* Stage rows. */}
      {triage && (
        <TimelineRow
          stageLabel="0 · Triage"
          title="Summary-only pass that tags + flags the video"
          accent={verdictTone(triage.triageOutput?.verdict).fg}
          cost={triageCost}
          tokens={tokensOf(triage)}
          model={triage.model}
          when={triage.createdAt}
        >
          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5}}>
            <Chip
              size="small"
              label={verdictTone(triage.triageOutput?.verdict).label}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                bgcolor: verdictTone(triage.triageOutput?.verdict).bg,
                color: verdictTone(triage.triageOutput?.verdict).fg,
                textTransform: 'capitalize',
              }}
            />
            {triage.triageOutput?.confidence != null && (
              <Chip
                size="small"
                label={`conf ${(triage.triageOutput.confidence * 100).toFixed(0)}%`}
                sx={{fontSize: 11, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
              />
            )}
            {(triage.triageOutput?.categoryTags ?? []).map(t => (
              <Chip key={t} size="small" label={humanizeTag(t)} sx={{fontSize: 11, bgcolor: pcolors.blue[50], color: pcolors.blue[700]}} />
            ))}
            {(triage.triageOutput?.riskFlags ?? []).map(f => (
              <Chip key={f} size="small" label={humanizeFlag(f)} sx={{fontSize: 11, bgcolor: pcolors.amber[50], color: pcolors.amber[700]}} />
            ))}
          </Box>
        </TimelineRow>
      )}

      {!shortCircuited && specialists.length > 0 && (
        <SpecialistsBatch
          specialists={specialists}
          totalCost={specialistCost}
          totalTokens={specialistTokens}
        />
      )}

      {final && (() => {
        const finalTone = agentScoreTone(final);
        return (
          <TimelineRow
            stageLabel="2 · Finalize"
            title="Meta-agent synthesizes specialists into an age-weighted verdict"
            accent={finalTone.fg}
            cost={finalCost}
            tokens={tokensOf(final)}
            model={final.model}
            when={final.createdAt}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
              <Chip
                size="small"
                label={`${final.score.toFixed(1)} · ${final.rating.toLowerCase()}`}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: finalTone.bg,
                  color: finalTone.fg,
                  textTransform: 'capitalize',
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            </Box>
          </TimelineRow>
        );
      })()}
    </Box>
  );
}

function SpecialistsBatch({
  specialists,
  totalCost,
  totalTokens,
}: {
  specialists: AgentScore[];
  totalCost: number;
  totalTokens: number;
}) {
  const [open, setOpen] = useState(true);
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `3px solid ${pcolors.blue[500]}`,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Typography sx={{fontSize: 10, fontWeight: 700, color: pcolors.textMuted, letterSpacing: 0.6, textTransform: 'uppercase'}}>
          1 · Specialists
        </Typography>
        <Typography sx={{fontSize: 12, color: pcolors.textPrimary, flex: 1, minWidth: 0}}>
          {specialists.length} agents, parallel fan-out
        </Typography>
        {totalCost > 0 && (
          <Typography sx={{fontSize: 11, color: pcolors.green[700], fontVariantNumeric: 'tabular-nums', fontWeight: 700}}>
            ${totalCost.toFixed(4)}
          </Typography>
        )}
        {totalTokens > 0 && (
          <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}>
            {totalTokens.toLocaleString()} tokens
          </Typography>
        )}
        <IconButton size="small" onClick={() => setOpen(o => !o)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25}}>
          {specialists
            .slice()
            // Alphabetical by specialization so the row for a given
            // agent stays put across re-evals and lines up with the
            // same row in the Eval Lab's side-by-side columns. The
            // concern-first sort made visual diffs harder to read.
            .sort((a, b) =>
              String(a.agent.specialization).localeCompare(
                String(b.agent.specialization),
              ),
            )
            .map(s => (
              <SpecialistRow key={s.id} score={s} />
            ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

function SpecialistRow({score}: {score: AgentScore}) {
  const tone = agentScoreTone(score);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        borderBottom: `1px dashed ${pcolors.borderLight}`,
        '&:last-of-type': {borderBottom: 'none'},
      }}
    >
      <Box
        sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: tone.strong, flex: 'none'}}
      />
      <Typography sx={{fontSize: 12, color: pcolors.textPrimary, flex: 1, minWidth: 0}} noWrap>
        {humanizeSpecialization(score.agent.specialization)}
      </Typography>
      <Typography sx={{fontSize: 11, color: tone.fg, fontVariantNumeric: 'tabular-nums', fontWeight: 600}}>
        {score.score.toFixed(1)}
      </Typography>
      <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right'}}>
        {costOf(score) > 0 ? `$${costOf(score).toFixed(4)}` : '—'}
      </Typography>
      <Typography sx={{fontSize: 10, color: pcolors.textMuted, fontFamily: 'monospace', minWidth: 80, textAlign: 'right'}} noWrap>
        {score.model?.split('/').pop() ?? ''}
      </Typography>
    </Box>
  );
}

function TimelineRow({
  stageLabel,
  title,
  accent,
  cost,
  tokens,
  model,
  when,
  children,
}: {
  stageLabel: string;
  title: string;
  accent: string;
  cost: number;
  tokens: number;
  model?: string | null;
  when?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: 10, fontWeight: 700, color: pcolors.textMuted, letterSpacing: 0.6, textTransform: 'uppercase'}}>
          {stageLabel}
        </Typography>
        <Typography sx={{fontSize: 12, color: pcolors.textPrimary, flex: 1, minWidth: 0}}>
          {title}
        </Typography>
        {cost > 0 && (
          <Typography sx={{fontSize: 11, color: pcolors.green[700], fontVariantNumeric: 'tabular-nums', fontWeight: 700}}>
            ${cost.toFixed(4)}
          </Typography>
        )}
        {tokens > 0 && (
          <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}>
            {tokens.toLocaleString()} tok
          </Typography>
        )}
        {model && (
          <Typography sx={{fontSize: 10, color: pcolors.textMuted, fontFamily: 'monospace'}} noWrap>
            {model.split('/').pop()}
          </Typography>
        )}
        {when && (
          <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
            {new Date(when).toLocaleTimeString()}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}
